// src/components/interview/SettingsPanel.tsx
import { useRef, useEffect, useState } from 'react'
import { t } from '@/i18n/translations'

function friendlyDeviceLabel(device: MediaDeviceInfo, fallback: string, lang: string): string {
  const raw = device.label?.trim()
  if (!raw) return fallback
  const lower = raw.toLowerCase()
  // Kameras
  if (/facing front|front camera/i.test(raw)) return t(lang, 'frontCamera')
  if (/facing back|back camera|rear camera/i.test(raw)) return t(lang, 'backCamera')
  if (lower === 'camera' || lower === 'kamera') return `${t(lang, 'camera')} (${device.deviceId.slice(0, 4)})`
  // Mikrofone
  if (/built.?in.*mic|internal.*mic/i.test(raw)) return t(lang, 'internalMicrophone')
  if (/headset.*mic/i.test(raw)) return t(lang, 'headsetMicrophone')
  // Lautsprecher
  if (/speakerphone/i.test(raw)) return t(lang, 'speakerphone')
  if (/earpiece/i.test(raw)) return t(lang, 'earpiece')
  if (/built.?in.*speaker|internal.*speaker/i.test(raw)) return t(lang, 'internalSpeaker')
  if (/headset(?!.*mic)|headphone/i.test(raw)) return t(lang, 'headphones')
  return raw
}

interface SettingsPanelProps {
  open: boolean
  onClose: () => void
  language: string
  cameraEnabled: boolean
  onToggleCamera: () => void
  streamRef: React.MutableRefObject<MediaStream | null>
  devices: {
    audioinput: MediaDeviceInfo[]
    videoinput: MediaDeviceInfo[]
    audiooutput: MediaDeviceInfo[]
  }
  selectedAudioInputId: string
  selectedVideoInputId: string
  selectedAudioOutputId: string
  sinkIdSupported: boolean
  onSwitchAudioInput: (id: string) => void | Promise<void>
  onSwitchVideoInput: (id: string) => void | Promise<void>
  onSwitchAudioOutput: (id: string) => void | Promise<void>
  onPlayTestTone: () => void
}

const BAR_COUNT = 10

export function SettingsPanel({
  open,
  onClose,
  language,
  cameraEnabled,
  onToggleCamera,
  streamRef,
  devices,
  selectedAudioInputId,
  selectedVideoInputId,
  selectedAudioOutputId,
  sinkIdSupported,
  onSwitchAudioInput,
  onSwitchVideoInput,
  onSwitchAudioOutput,
  onPlayTestTone,
}: SettingsPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLVideoElement>(null)
  const [audioLevels, setAudioLevels] = useState<number[]>(() => Array(BAR_COUNT).fill(0))
  const [switchError, setSwitchError] = useState<string | null>(null)

  // Device switches reject when the chosen device was just unplugged or is
  // held by another app (OverconstrainedError/NotReadableError/NotFoundError).
  // Without a catch the dropdown silently "doesn't react" and the rejection
  // is unhandled — surface a short message instead.
  const guardSwitch = (fn: (id: string) => void | Promise<void>) => (id: string) => {
    setSwitchError(null)
    Promise.resolve(fn(id)).catch((err) => {
      console.warn('Device switch failed:', err)
      setSwitchError(t(language, 'deviceSwitchFailed'))
    })
  }

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Camera preview
  useEffect(() => {
    const video = previewRef.current
    const stream = streamRef.current
    if (!video || !stream || !open || !cameraEnabled) return
    video.srcObject = stream
    return () => { video.srcObject = null }
  }, [open, cameraEnabled, streamRef, selectedVideoInputId])

  // Audio level monitoring
  useEffect(() => {
    if (!open) return
    const stream = streamRef.current
    if (!stream || !stream.getAudioTracks().length) return

    const ctx = new AudioContext()
    const analyser = ctx.createAnalyser()
    analyser.fftSize = 256
    ctx.createMediaStreamSource(stream).connect(analyser)
    const data = new Uint8Array(analyser.frequencyBinCount)

    let raf: number
    const tick = () => {
      analyser.getByteFrequencyData(data)
      // Sample BAR_COUNT evenly spaced bins
      const step = Math.floor(data.length / BAR_COUNT)
      const levels: number[] = []
      for (let i = 0; i < BAR_COUNT; i++) {
        levels.push(data[i * step] / 255)
      }
      setAudioLevels(levels)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      analyser.disconnect()
      ctx.close()
    }
  }, [open, streamRef, selectedAudioInputId])

  return (
    <>
      {/* Overlay */}
      <div
        className={`iv-settings-overlay${open ? ' iv-settings-overlay--open' : ''}`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`iv-settings-panel${open ? ' iv-settings-panel--open' : ''}`}
      >
        {/* Header */}
        <div className="iv-settings-header">
          <span className="iv-settings-title">{t(language, 'settings')}</span>
          <button className="iv-settings-close" onClick={onClose}>✕</button>
        </div>

        {switchError && (
          <div style={{ color: '#f87171', fontSize: 12, padding: '8px 16px' }}>
            {switchError}
          </div>
        )}

        {/* Kamera */}
        <div className="iv-settings-section">
          <div className="iv-settings-section-header">
            <div className="iv-settings-section-label">
              <svg viewBox="0 0 24 24">
                <path d="M23 7l-7 5 7 5V7z" />
                <rect x="1" y="5" width="15" height="14" rx="2" />
              </svg>
              {t(language, 'camera')}
            </div>
            <button
              className={`iv-toggle${cameraEnabled ? ' iv-toggle--on' : ''}`}
              onClick={onToggleCamera}
            >
              <div className="iv-toggle-knob" />
            </button>
          </div>
          <div className="iv-settings-row">
            <div className="iv-camera-preview">
              {cameraEnabled ? (
                <video ref={previewRef} autoPlay muted playsInline />
              ) : (
                <div className="iv-camera-preview-off">
                  <span style={{ fontSize: 24 }}>📷</span>
                  <span>{t(language, 'off')}</span>
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div className="iv-device-label">{t(language, 'device')}</div>
              <select
                className="iv-device-select"
                value={selectedVideoInputId}
                onChange={(e) => guardSwitch(onSwitchVideoInput)(e.target.value)}
              >
                {devices.videoinput.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {friendlyDeviceLabel(d, `${t(language, 'camera')} ${d.deviceId.slice(0, 8)}`, language)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Mikrofon */}
        <div className="iv-settings-section">
          <div className="iv-settings-section-header">
            <div className="iv-settings-section-label">
              <svg viewBox="0 0 24 24">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
              </svg>
              {t(language, 'microphone')}
            </div>
          </div>
          <div className="iv-settings-row">
            <div>
              <div className="iv-device-label">{t(language, 'level')}</div>
              <div className="iv-audio-level">
                {audioLevels.map((l, i) => (
                  <div
                    key={i}
                    className="iv-audio-level-bar"
                    style={{ height: `${Math.max(10, l * 100)}%` }}
                  />
                ))}
              </div>
              <div className="iv-audio-level-hint">{t(language, 'speakToTest')}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div className="iv-device-label">{t(language, 'device')}</div>
              <select
                className="iv-device-select"
                value={selectedAudioInputId}
                onChange={(e) => guardSwitch(onSwitchAudioInput)(e.target.value)}
              >
                {devices.audioinput.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {friendlyDeviceLabel(d, `${t(language, 'microphone')} ${d.deviceId.slice(0, 8)}`, language)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Lautsprecher */}
        <div className="iv-settings-section">
          <div className="iv-settings-section-header">
            <div className="iv-settings-section-label">
              <svg viewBox="0 0 24 24">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
              {t(language, 'speaker')}
            </div>
          </div>
          <div className="iv-settings-row">
            <div>
              <div className="iv-device-label">{t(language, 'test')}</div>
              <button className="iv-test-tone-btn" onClick={onPlayTestTone}>
                🔊 {t(language, 'playTone')}
              </button>
            </div>
            <div style={{ flex: 1 }}>
              <div className="iv-device-label">{t(language, 'device')}</div>
              {sinkIdSupported ? (
                <select
                  className="iv-device-select"
                  value={selectedAudioOutputId}
                  onChange={(e) => guardSwitch(onSwitchAudioOutput)(e.target.value)}
                >
                  {devices.audiooutput.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {friendlyDeviceLabel(d, `${t(language, 'speaker')} ${d.deviceId.slice(0, 8)}`, language)}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 6 }}>
                  {t(language, 'speakerSelectionNotSupported')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
